import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import AnimatedSection from "@/components/common/AnimatedSection";
import PageTransition from "@/components/layout/PageTransition";
import { serviceSlugLookup } from "@/config/servicesMenu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const topicImages = [
  "https://images.unsplash.com/photo-1606811971618-4486d14f3f99?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1609840114035-3c981b782dfe?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=1200&q=80",
];

const beforeAfterPairs = [
  {
    title: "Clinical Profile Correction",
    before:
      "https://images.unsplash.com/photo-1544717301-9cdcb1f5940f?auto=format&fit=crop&w=1000&q=80",
    after:
      "https://images.unsplash.com/photo-1593022356769-11f762e25ed9?auto=format&fit=crop&w=1000&q=80",
  },
  {
    title: "Functional Bite Restoration",
    before:
      "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=1000&q=80",
    after:
      "https://images.unsplash.com/photo-1629909615184-74f495363b67?auto=format&fit=crop&w=1000&q=80",
  },
  {
    title: "Smile Symmetry Enhancement",
    before:
      "https://images.unsplash.com/photo-1606811971618-4486d14f3f99?auto=format&fit=crop&w=1000&q=80",
    after:
      "https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=1000&q=80",
  },
];

const buildLongFormContent = (title, category) => [
  `${title} is not a one-size-fits-all treatment pathway; it is a strategic clinical journey that combines diagnosis, planning, biomaterials, and patient comfort into one coordinated workflow. At Kavlekar Dental, we begin by understanding your functional needs, aesthetic expectations, existing oral condition, and long-term oral health goals before deciding how this procedure should be tailored for you. This helps us design a treatment sequence that is realistic, predictable, and personalized rather than rushed or generic.`,
  `Our approach in ${category} starts with detailed records and case mapping. We evaluate tooth structure, gum condition, bite forces, jaw relation, smile line, and oral habits that could influence the treatment result. This deeper analysis prevents avoidable compromises and helps us set a clear objective for every stage of your care. Once the plan is finalized, we explain each phase in simple language so you know what will happen, why it is recommended, and what outcomes to expect over time.`,
  `The procedural phase is performed with strict sterilization, precision instrumentation, and careful tissue handling. Whether your case is straightforward or complex, we maintain a comfort-first protocol and evidence-based treatment sequence. We focus on preserving healthy structures wherever possible and restoring strength, function, and appearance with balanced clinical judgment. This means your treatment is designed not only to look good immediately but also to perform reliably in everyday life such as speaking, chewing, and smiling with confidence.`,
  `Post-procedure stability is equally important. After the active treatment phase, we guide you through maintenance, hygiene recommendations, and review intervals that protect long-term success. Many treatment outcomes depend on follow-up discipline, and we support patients with practical instructions that are easy to follow in routine life. We also monitor adaptation and comfort, making fine adjustments whenever needed so the result continues to feel natural and stable, not just clinically acceptable on paper.`,
  `A key reason patients choose Kavlekar Dental is our blend of advanced technology and transparent communication. We do not rely on technical complexity alone; we ensure every recommendation is linked to your personal goals and oral condition. Through this patient-centered model, ${title} becomes a thoughtful care pathway with clear milestones, safety checks, and measurable outcomes. The intention is to reduce anxiety, improve treatment understanding, and build trust at every stage of your dental journey.`,
  `In summary, ${title} at our clinic is driven by precision, personalization, and long-term accountability. From the first consultation to the final review, each decision is made to improve function, preserve oral health, and deliver a confident smile that feels authentic to you. If you are exploring this treatment, our team can provide a clear case-specific consultation and walk you through timelines, options, and expected outcomes so you can make an informed decision with confidence and clarity.`,
];

const buildFaqs = (title) => [
  {
    question: `Am I a suitable candidate for ${title}?`,
    answer:
      "Most candidates are identified after clinical evaluation, imaging, and bite analysis. We assess oral condition and goals before confirming suitability.",
  },
  {
    question: `How long does ${title} usually take from start to finish?`,
    answer:
      "Treatment duration depends on case complexity, healing needs, and planning sequence. We provide a realistic timeline during consultation.",
  },
  {
    question: `Will the ${title} process be painful?`,
    answer:
      "With modern anesthesia, precise protocols, and comfort-focused care, most patients report a manageable and smooth treatment experience.",
  },
  {
    question: `How long can results of ${title} last?`,
    answer:
      "With good oral hygiene, periodic reviews, and proper maintenance, results can remain stable for many years with excellent function.",
  },
  {
    question: `Can ${title} be combined with other dental procedures?`,
    answer:
      "Yes, integrated care plans are common. We often combine restorative, aesthetic, or periodontal steps when clinically appropriate.",
  },
  {
    question: `What precautions should I follow after ${title}?`,
    answer:
      "Follow the prescribed hygiene protocol, diet recommendations, and review schedule to protect healing and long-term treatment success.",
  },
  {
    question: `How do I begin treatment planning for ${title}?`,
    answer:
      "Start with a consultation at Kavlekar Dental. We map your case, explain options, and create a stepwise treatment plan.",
  },
];

export default function ServiceDetailPage() {
  const { serviceSlug } = useParams();
  const service = serviceSlugLookup[serviceSlug] || {
    title: "Dental Service",
    category: "Kavlekar Dental",
  };
  const longContent = buildLongFormContent(service.title, service.category);
  const faqs = buildFaqs(service.title);

  return (
    <PageTransition testId="service-detail-page">
      <section className="relative isolate overflow-hidden" data-testid="service-detail-page-hero">
        <motion.img
          src="https://images.unsplash.com/photo-1606811971618-4486d14f3f99?auto=format&fit=crop&w=2000&q=80"
          alt={service.title}
          className="h-[52vh] min-h-[360px] w-full object-cover"
          animate={{ scale: [1.01, 1.08, 1.01] }}
          transition={{ duration: 13, repeat: Infinity, ease: "easeInOut" }}
          data-testid="service-detail-page-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/78 via-[#1a1a1a]/55 to-[#1a1a1a]/30" />
        <div className="absolute inset-0 mx-auto flex max-w-7xl items-end px-6 pb-10 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl rounded-2xl border border-white/30 bg-white/10 p-5 backdrop-blur-md md:p-7"
            data-testid="service-detail-page-hero-text-panel"
          >
            <p
              className="font-[Manrope] text-xs uppercase tracking-[0.18em] text-[#f2e6c9]"
              data-testid="service-detail-page-hero-category"
            >
              {service.category}
            </p>
            <h1
              className="mt-2 font-[Playfair_Display] text-4xl text-white md:text-6xl"
              data-testid="service-detail-page-hero-title"
            >
              {service.title}
            </h1>
          </motion.div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-24">
        <AnimatedSection testId="service-detail-long-content-section">
          <div className="rounded-2xl border border-[#e8d6ad] bg-white p-7 md:p-10">
            <h2
              className="font-[Playfair_Display] text-3xl text-[#1a1a1a] md:text-4xl"
              data-testid="service-detail-long-content-title"
            >
              Detailed Overview
            </h2>
            <div className="mt-5 space-y-4">
              {longContent.map((paragraph, index) => (
                <motion.p
                  key={paragraph.slice(0, 40)}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: index * 0.04 }}
                  className="font-[Manrope] text-sm leading-relaxed text-[#555555]"
                  data-testid={`service-detail-long-content-paragraph-${index + 1}`}
                >
                  {paragraph}
                </motion.p>
              ))}
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection className="mt-14" testId="service-detail-topic-images-section">
          <h2
            className="font-[Playfair_Display] text-3xl text-[#1a1a1a] md:text-4xl"
            data-testid="service-detail-topic-images-title"
          >
            Clinical Highlights
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {topicImages.map((image, index) => (
              <motion.article
                key={image}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.05 }}
                whileHover={{ y: -5, scale: 1.01, rotate: index % 2 === 0 ? -0.5 : 0.5 }}
                className="overflow-hidden rounded-2xl border border-[#f2e6c9] bg-white"
                data-testid={`service-detail-topic-image-card-${index + 1}`}
              >
                <img
                  src={image}
                  alt={`${service.title} visual ${index + 1}`}
                  className="aspect-[4/3] w-full object-cover transition-transform duration-500 hover:scale-105"
                  loading="lazy"
                  data-testid={`service-detail-topic-image-${index + 1}`}
                />
              </motion.article>
            ))}
          </div>
        </AnimatedSection>

        <AnimatedSection className="mt-14" testId="service-detail-before-after-section">
          <h2
            className="font-[Playfair_Display] text-3xl text-[#1a1a1a] md:text-4xl"
            data-testid="service-detail-before-after-title"
          >
            Before & After Cases
          </h2>
          <div className="mt-6 grid gap-5 lg:grid-cols-3">
            {beforeAfterPairs.map((pair, index) => (
              <motion.article
                key={pair.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.06 }}
                className="overflow-hidden rounded-2xl border border-[#f2e6c9] bg-white"
                data-testid={`service-detail-before-after-card-${index + 1}`}
              >
                <div className="grid grid-cols-2">
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    className="relative overflow-hidden"
                    data-testid={`service-detail-before-button-${index + 1}`}
                  >
                    <img
                      src={pair.before}
                      alt={`${pair.title} before`}
                      className="aspect-[3/4] w-full object-cover"
                      loading="lazy"
                      data-testid={`service-detail-before-image-${index + 1}`}
                    />
                    <span
                      className="absolute left-2 top-2 rounded-full bg-[#1a1a1a]/80 px-2 py-1 font-[Manrope] text-[10px] uppercase tracking-wider text-white"
                      data-testid={`service-detail-before-label-${index + 1}`}
                    >
                      Before
                    </span>
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    className="relative overflow-hidden"
                    data-testid={`service-detail-after-button-${index + 1}`}
                  >
                    <img
                      src={pair.after}
                      alt={`${pair.title} after`}
                      className="aspect-[3/4] w-full object-cover"
                      loading="lazy"
                      data-testid={`service-detail-after-image-${index + 1}`}
                    />
                    <span
                      className="absolute left-2 top-2 rounded-full bg-[#c5a059]/90 px-2 py-1 font-[Manrope] text-[10px] uppercase tracking-wider text-white"
                      data-testid={`service-detail-after-label-${index + 1}`}
                    >
                      After
                    </span>
                  </motion.button>
                </div>
                <p
                  className="border-t border-[#f2e6c9] px-4 py-3 font-[Manrope] text-sm text-[#555555]"
                  data-testid={`service-detail-before-after-caption-${index + 1}`}
                >
                  {pair.title}
                </p>
              </motion.article>
            ))}
          </div>
        </AnimatedSection>

        <AnimatedSection className="mt-14" testId="service-detail-faq-section">
          <h2
            className="font-[Playfair_Display] text-3xl text-[#1a1a1a] md:text-4xl"
            data-testid="service-detail-faq-title"
          >
            FAQs on {service.title}
          </h2>

          <Accordion
            type="single"
            collapsible
            className="mt-6 rounded-2xl border border-[#f2e6c9] bg-white px-5 md:px-7"
            data-testid="service-detail-faq-accordion"
          >
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.36, delay: index * 0.05 }}
                whileTap={{ scale: 0.995 }}
                data-testid={`service-detail-faq-item-wrapper-${index + 1}`}
              >
                <AccordionItem value={`faq-${index + 1}`}>
                  <AccordionTrigger
                    className="font-[Playfair_Display] text-xl text-[#1a1a1a] hover:no-underline"
                    data-testid={`service-detail-faq-question-${index + 1}`}
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent
                    className="font-[Manrope] text-sm leading-relaxed text-[#c5a059]"
                    data-testid={`service-detail-faq-answer-${index + 1}`}
                  >
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </AnimatedSection>

        <AnimatedSection className="mt-12" testId="service-detail-back-link-section">
          <Link
            to="/services"
            className="inline-flex rounded-full border border-[#c5a059] bg-white px-6 py-3 font-[Manrope] text-xs font-semibold uppercase tracking-[0.15em] text-[#b08d45] transition-colors hover:bg-[#f9f4e8]"
            data-testid="service-detail-back-to-services-link"
          >
            Back to Services
          </Link>
        </AnimatedSection>
      </div>
    </PageTransition>
  );
}
